/*
 * product-inventory.js — Enterprise 2.3.0, patched for custom.stock_state
 *
 * WHAT CHANGED (4 marked blocks, everything else is stock):
 *   1. initLazySection()   — reads the two new data attributes on init
 *   2. handleVariantChange() — passes them through on variant switch
 *   3. updateInventory()   — stock_state takes precedence over count/policy
 *   4. notice text         — 'gettable' prints ship_estimate when present
 *
 * BEHAVIOUR WHEN stock_state IS BLANK: identical to stock theme. The metafield
 * is empty on the whole catalogue today, so this is a safe no-op until the
 * rollup starts populating it.
 *
 * MAPPING
 *   on_hand      -> in_stock     (theme.strings.inStock)
 *   gettable     -> backordered  (ship_estimate, else theme.strings.backordered)
 *   unavailable  -> none         (theme.strings.noStock)
 */

if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      if (this.hidden) {
        this.initLazySection();
      } else {
        window.initLazyScript(this, this.initLazySection.bind(this));
      }
    }

    initLazySection() {
      this.section = this.closest('.js-product');
      this.inventoryNotice = this.querySelector('.js-inventory-notice');
      this.urgencyMessage = this.querySelector('.js-inventory-urgency');
      this.indicatorBar = this.querySelector('.js-inventory-indicator');

      // Bind to on:variant:change event for this product
      this.section.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      // Init
      this.updateInventory(
        parseInt(this.dataset.inventoryQuantity, 10),
        this.dataset.variantAvailable === 'true',
        this.dataset.inventoryPolicy,
        // [1] CHANGED: seed from data attributes rendered by Liquid
        this.dataset.stockState,
        this.dataset.shipEstimate
      );
    }

    /**
     * Gets the inventory data for all product variants
     * @returns {?object}
     */
    getVariantInventory() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      if (evt.detail.variant) {
        const inventory = this.getVariantInventory();
        this.updateInventory(
          inventory.inventory_quantity,
          inventory.available,
          inventory.inventory_policy,
          // [2] CHANGED: carry the metafields through on variant switch
          inventory.stock_state,
          inventory.ship_estimate
        );
      } else {
        this.updateInventory(0, false);
        this.hidden = true;
      }
    }

    /**
     *
     * Updates the inventory notice
     * @param {number} count - the inventory quantity available
     * @param {boolean} available - whether current variant is available
     * @param {string} inventoryPolicy - whether product continues selling when out of stock
     * @param {string} [stockState] - custom.stock_state: on_hand | gettable | unavailable
     * @param {string} [shipEstimate] - custom.ship_estimate, shown for 'gettable'
     */
    updateInventory(count, available, inventoryPolicy, stockState, shipEstimate) {
      if (count === 0 && !available && this.hidden && !stockState) {
        return;
      }

      let inventoryLevel;

      // [3] CHANGED: stock_state wins when set. Falls through to the original
      // count/policy logic when blank or unrecognised.
      if (stockState === 'on_hand') {
        inventoryLevel = count > 0 && count <= parseInt(this.dataset.thresholdVeryLow, 10)
          ? 'very_low'
          : 'in_stock';
      } else if (stockState === 'gettable') {
        inventoryLevel = 'backordered';
      } else if (stockState === 'unavailable') {
        inventoryLevel = 'none';
      } else if (count <= 0) {
        if (inventoryPolicy === 'continue') {
          inventoryLevel = 'backordered';
        } else if (available) {
          inventoryLevel = 'in_stock';
        } else {
          inventoryLevel = 'none';
        }
      } else if (count <= parseInt(this.dataset.thresholdVeryLow, 10)) {
        inventoryLevel = 'very_low';
      } else if (count <= parseInt(this.dataset.thresholdLow, 10)) {
        inventoryLevel = 'low';
      } else {
        inventoryLevel = 'normal';
      }

      // [3b] CHANGED: a state we set deliberately always shows. The
      // hide_no_stock_backordered setting still governs the inferred
      // backordered state, just not an explicit 'gettable'.
      const forceShow = Boolean(stockState);

      if (forceShow || ((this.dataset.showNotice === 'always'
        || (this.dataset.showNotice === 'low' && inventoryLevel.includes('low')))
        && !(inventoryLevel === 'backordered' && this.dataset.showNoStockBackordered === 'false'))) {
        this.hidden = false;

        // Set the inventory level data attribute
        this.setAttribute('data-inventory-level', inventoryLevel);

        // Determine whether to show the count or not
        if (inventoryLevel === 'backordered') {
          // [4] CHANGED: prefer the per-variant lead time copy
          this.inventoryNotice.innerText = (stockState === 'gettable' && shipEstimate)
            ? shipEstimate
            : theme.strings.backordered;
        } else if (inventoryLevel !== 'in_stock' && (this.dataset.showCount === 'always' || (this.dataset.showCount === 'low' && inventoryLevel.includes('low')))) {
          this.inventoryNotice.innerText = theme.strings.onlyXLeft.replace('[quantity]', count);
        } else if (inventoryLevel === 'very_low') {
          this.inventoryNotice.innerText = theme.strings.veryLowStock;
        } else if (inventoryLevel === 'low') {
          this.inventoryNotice.innerText = theme.strings.lowStock;
        } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
          this.inventoryNotice.innerText = theme.strings.inStock;
        } else if (inventoryLevel === 'none') {
          this.inventoryNotice.innerText = theme.strings.noStock;
        }

        // Update urgency message if needed
        if (this.urgencyMessage) {
          if (inventoryLevel === 'very_low') {
            this.urgencyMessage.innerHTML = this.dataset.textVeryLow;
          } else if (inventoryLevel === 'low') {
            this.urgencyMessage.innerHTML = this.dataset.textLow;
          } else if (inventoryLevel === 'backordered') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStockBackordered;
          } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
            this.urgencyMessage.innerHTML = this.dataset.textNormal;
          } else if (inventoryLevel === 'none') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStock;
          }
        }

        // Update the bar indicator
        if (this.indicatorBar) {
          this.indicatorBar.hidden = false;

          let newWidth;
          if ((count >= this.dataset.scale || inventoryLevel === 'in_stock') && inventoryLevel !== 'backordered') {
            newWidth = 100;
          } else {
            newWidth = ((100 / parseInt(this.dataset.scale, 10)) * count).toFixed(1);
          }
          this.indicatorBar.querySelector('span').style.width = `${newWidth}%`;
        }
      } else {
        this.hidden = true;
      }
    }
  }

  customElements.define('product-inventory', ProductInventory);
}